import { useMemo, useState } from 'react';
import { DocumentIcon } from '@heroicons/react/24/outline';
import { FormInput } from '@/components/FormInput';
import { FormSelect } from '@/components/FormSelect';
import type { DpRequest } from '../../../../../shared/dpRequests';
import { flattenDpDocuments, matchesDocumentSearch } from '../../utils/dpDocuments';
import { REQUEST_TYPE_LABELS } from '../../dpRequests.definitions';
import type { DpDocumentsPanelProps } from './dpDocumentsPanel.types';

const TYPE_OPTIONS = [
    { value: 'all', label: 'Todos os tipos' },
    ...Object.entries(REQUEST_TYPE_LABELS).map(([value, label]) => ({ value, label })),
];

function formatDate(date: Date): string {
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export function DpDocumentsPanel({ requests, emptyText }: DpDocumentsPanelProps) {
    const [search, setSearch] = useState('');
    const [type, setType] = useState('all');

    const rows = useMemo(() => flattenDpDocuments(requests as DpRequest[]), [requests]);
    const visible = rows.filter(row => (type === 'all' || row.typeLabel === REQUEST_TYPE_LABELS[type as DpRequest['type']]) && matchesDocumentSearch(row, search));

    return (
        <div className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,240px)]">
                <FormInput
                    name="document-search"
                    placeholder="Buscar por pessoa, e-mail ou arquivo"
                    value={search}
                    onChange={event => setSearch(event.target.value)}
                />
                <FormSelect name="document-type" value={type} onChange={event => setType(event.target.value)} options={TYPE_OPTIONS} />
            </div>

            {visible.length === 0 ? (
                <p className="rounded-2xl border border-dashed border-raro-gray-200 px-5 py-10 text-center text-sm text-raro-gray-500">{emptyText}</p>
            ) : (
                <ul className="overflow-hidden rounded-2xl border border-raro-gray-200 bg-white shadow-sm divide-y divide-raro-gray-100">
                    {visible.map(row => (
                        <li key={row.key} className="flex items-center gap-3 px-4 py-3">
                            <DocumentIcon className="h-5 w-5 shrink-0 text-primary-500" aria-hidden="true" />
                            <div className="min-w-0 flex-1">
                                <p className="truncate text-sm font-semibold text-raro-gray-900">{row.attachment.name}</p>
                                <p className="truncate text-xs text-raro-gray-500">
                                    {row.collaboratorName || row.collaboratorEmail} · {row.typeLabel} · {formatDate(row.createdAt)}
                                </p>
                            </div>
                            <a
                                href={row.attachment.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="shrink-0 rounded-lg px-3 py-1.5 text-sm font-semibold text-primary-700 hover:bg-primary-50"
                            >
                                Abrir
                            </a>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
